import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Spinner } from '../components/widgets';
import { PageWrapper } from '../components/partials';
import IntegrationHomepage from './containers/IntegrationHomepage';
import IntegrationRequestForm from './containers/IntegrationRequestForm';
import { HIDDEN_STATUSES, REQUEST_TYPES } from './consts';
import { getStatusForType, isFormType } from './utils';
import { getAdminModeEnabled, getIsUserAdmin } from '../selectors/user';
import css from './page.css';

const BASE_PATH = '/react/integrations';

const PartnerIntegration = ({ requests, loading, loaded }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const adminMode = useSelector(getAdminModeEnabled);
    const isStaff = useSelector(getIsUserAdmin);
    const canViewHiddenStatus = adminMode || isStaff;

    const params = new URLSearchParams(location.search);
    const rawRequestId = params.get('request_id');
    const requestId = rawRequestId ? Number(rawRequestId) : null;
    const typeParam = (params.get('type') || '').trim().toLowerCase();

    const existingRequest = requestId ? requests.find(r => r.id === requestId) : null;
    const requestedType = existingRequest?.request_type || typeParam;
    const isRouteTypeValid = Boolean(
        requestedType && Object.values(REQUEST_TYPES).includes(requestedType) && isFormType(requestedType)
    );

    const isExistingRoute = Boolean(requestId && existingRequest);
    const isNewRoute = !requestId && isRouteTypeValid;
    const hasRoute = isExistingRoute || isNewRoute;

    const { status } = isExistingRoute ? { status: existingRequest.status } : getStatusForType(requests, requestedType);
    const isHiddenStatus = HIDDEN_STATUSES.includes(status);

    const isValidRoute = hasRoute && (!isHiddenStatus || canViewHiddenStatus);

    useEffect(() => {
        if (hasRoute && loaded && !isValidRoute) {
            navigate(BASE_PATH, { replace: true });
        }
    }, [hasRoute, isValidRoute, loaded, navigate]);

    if (hasRoute && loading && !loaded) {
        return (
            <PageWrapper>
                <Spinner expanded show />
            </PageWrapper>
        );
    }

    return (
        <PageWrapper className={css.container} disablePadding>
            {isValidRoute ? (
                <IntegrationRequestForm type={requestedType} requestType={requestedType} requestId={requestId} />
            ) : (
                <IntegrationHomepage />
            )}
        </PageWrapper>
    );
};

PartnerIntegration.propTypes = {
    requests: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            request_type: PropTypes.string,
            status: PropTypes.string,
            updated_at: PropTypes.string,
        })
    ).isRequired,
    loading: PropTypes.bool.isRequired,
    loaded: PropTypes.bool.isRequired,
};

export default PartnerIntegration;
